import { useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import L from "leaflet";

import "components/maps/utils/leafletCompat";
import AuthContext from "context/AuthContext";
import { CARTO_TILE_URL, CARTO_TILE_OPTIONS } from 'core/mapsConfig';

const LeafletMapAssetAndDevices = ({ asset, devices, mapId = "map-devices-js", height = "300px", show = false }) => {
    const { panel } = useContext(AuthContext);
    const { t: _Tc } = useTranslation("common");
    const mapRef = useRef(null);
    const assetLayerRef = useRef(new L.layerGroup());
    const devicesLayerRef = useRef(new L.layerGroup());
    const zoom = 15;
    useEffect(() => {
        initializeMap();
        return () => {
            if (mapRef.current) {
                if (mapRef.current.off) mapRef.current.off();
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [asset]);

    useEffect(() => {
        if (devices) {
            addDevicesToMap();
        }
    }, [devices]);

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.invalidateSize(true);
        }
    }, [show]);

    const initializeMap = () => {
        let center = panel?.coords || [0, 0];
        if (asset?.lat && asset?.lng) {
            center = [asset.lat, asset.lng];
        } else if (devices && devices.length > 0 && devices[0].lat && devices[0].lng) {
            center = [devices[0].lat, devices[0].lng];
        }
        const validCenter = Array.isArray(center) && center.length === 2 && Number.isFinite(center[0]) && Number.isFinite(center[1]);
        if (!validCenter) return;

        if (!mapRef.current) {
            mapRef.current = L.map(mapId, {
                center: center,
                zoom: zoom,
            });

            L.tileLayer(CARTO_TILE_URL, CARTO_TILE_OPTIONS).addTo(mapRef.current);

            assetLayerRef.current.addTo(mapRef.current);
            devicesLayerRef.current.addTo(mapRef.current);

            mapRef.current.invalidateSize(true);
        }
        addAssetToMap();
        addDevicesToMap();
    };

    const addAssetToMap = () => {
        assetLayerRef.current.clearLayers();

        if (!asset || !asset.lat || !asset.lng) return false;

        if (!mapRef.current || !asset || !asset.lat || !asset.lng) return false;
        const zoomNow = mapRef.current.getZoom();
        const sizePx = Math.max(22, Math.round(36 - zoomNow * 0.7)); // icono más pequeño
        const icon = makeDivIcon(sizePx, "#49cb5c", "#fff", "50%", 1);
        const marker = L.marker([asset.lat, asset.lng], {
            icon,
            zIndexOffset: 0,
        }).bindPopup(`<b>${_Tc("names.asset")}</b>`);

        assetLayerRef.current.addLayer(marker);
    };

    const addDevicesToMap = () => {
        devicesLayerRef.current.clearLayers();
        if (!devices || devices.length === 0) return false;
        if (!mapRef.current) return false;
        const zoomNow = mapRef.current.getZoom();
        const sizePx = Math.max(10, Math.round(14 - zoomNow * 0.2)); // icono cuadrado pequeño
        const icon = makeDivIcon(
            sizePx,
            "#1774fa", // azul más intenso
            "#fff", // borde oscuro
            "0px", // esquinas no redondeadas -> cuadrado
            2, // z-index
        );
        for (const device of devices) {
            const marker = L.marker([device.lat, device.lng], {
                icon,
                zIndexOffset: 1000,
            }).bindPopup(`<b>${device.uuid}</b>`);
            devicesLayerRef.current.addLayer(marker);
        }
    };

    // Use divIcon markers so they are always visible and not dependent on meter->degree conversions
    const makeDivIcon = (sizePx, bgColor, borderColor, borderRadius = "50%", zIndex = 1, extraStyle = "") => {
        return L.divIcon({
            className: "",
            html: `<div style="width:${sizePx}px;height:${sizePx}px;background:${bgColor};border:1px solid ${borderColor};border-radius:${borderRadius};box-shadow:0 2px 8px 1px rgba(0,0,0,0.25);z-index:${zIndex};display:flex;align-items:center;justify-content:center;${extraStyle}"></div>`,
            iconSize: [sizePx, sizePx],
            iconAnchor: [Math.round(sizePx / 2), Math.round(sizePx / 2)],
        });
    };
    return (
        <div
            id={mapId}
            style={{
                height: height,
                width: "100%",
                borderRadius: "4px",
                overflow: "hidden",
                zIndex: 0,
            }}
        />
    );
};

export default LeafletMapAssetAndDevices;
